import { OPERATORS, EFFECTS } from './schema';
/**
 * Helpers used by the Behavior tab UI (BehaviorPanel + RuleEditorDialog).
 *
 * Separated from evaluate.js (the runtime evaluator) because these are
 * UI-only concerns: walking the structure tree to enumerate named
 * fields, summarizing a rule for the card list, and validating a rules
 * array with admin-facing error messages.
 */
import { getElementId } from '../../utils/format';

/**
 * Walk the form structure tree and collect every input-bearing element
 * with a non-empty `name`. Returns the list in source order. Each entry
 * carries enough info for the rule editor to render type-appropriate
 * value controls (e.g. show the source field's options when the rule
 * is `equals` against a dropdown).
 *
 * Recurses into `column_group` and `line_group` children.
 *
 * Elements without `name` (titles, hints, buttons, separators, layout
 * containers themselves) are skipped — rules can only reference named
 * fields.
 */
export function collectNamedFields(structure) {
  const out = [];
  if (!Array.isArray(structure)) return out;
  for (const el of structure) {
    if (!el || typeof el !== 'object') continue;
    if (getElementId(el)) {
      const eid = getElementId(el);
      out.push({
        name:    eid,
        label:   el.label || eid,
        type:    el.type,
        // Surface options for the rule editor's value dropdown.
        // dropdown / radio: single-select options
        // multiselect / checkbox: multi-select options
        // 0.47.66 - Only a manual list: a field bound to a library
        // (or an API response) keeps the manual options it had before
        // the switch, which are not its choices any more.
        options: (el.source === 'library' || el.source === 'api')
          ? null : (Array.isArray(el.options) ? el.options : null),
        // 0.47.66 - Where the choices come from, and the element, so
        // the rule editor can list a library's values.
        source: el.source || 'manual',
        library: el.source === 'library' ? (el.library || '') : '',
        element: el,
        required: !!el.required,
      });
      // 0.49.74 - A closable Alert: its Close, and whether it is closed.
      if (el.type === 'alert' && el.closable) {
        out.push({ name: `${eid}.close`, label: `${el.title || eid} - Close`, type: 'button', options: null, required: false });
      }
      // 0.49.73 - A Phone field's country (ISO alpha-2): <field>_country.
      if (el.type === 'phone') {
        out.push({ name: `${eid}_country`, label: `${el.label || eid} - country (two letters)`, type: 'text',
                   options: null, required: false });
      }
      // 0.48.64 - A table with an Edit link on its rows: the click.
      if (el.type === 'table' && el.row_edit) {
        out.push({ name: `${eid}.edit`, label: `${el.label || eid} - Edit link of a row`, type: 'button',
                   options: null, required: false });
        // 0.49.69 - Which row it was, by its place: 1, 2, 3... (manual
        // and API tables; a library row has its own id).
        if (el.source !== 'library') out.push({ name: `${eid}_row_number`, label: `${el.label || eid} - number of the row clicked`,
                   type: 'number', synthType: 'number', options: null, required: false });
      }
      // 0.48.63 - A Modal's two buttons, as clickable subjects:
      // "edit_phone.ok was just clicked", "edit_phone.cancel ...".
      if (el.type === 'column_group' && el.modal) {
        const baseLabel = el.modal_title || eid;
        out.push({ name: `${eid}.ok`, label: `${baseLabel} - OK button`, type: 'button',
                   options: null, required: false });
        out.push({ name: `${eid}.cancel`, label: `${baseLabel} - Cancel (or x, Esc)`, type: 'button',
                   options: null, required: false });
      }
      // 0.36.712 — Library-backed tables publish four derived keys
      // (__total, __selected_count, __all_selected, __any_selected)
      // on every relevant change. Surface them as rule-picker fields
      // so admin can write rules like "when table__all_selected
      // equals true, then set_label of button". `synthType` drives
      // the value control in the rule editor: boolean fields get a
      // true/false picker (saved as actual bool, so === comparison
      // matches); numeric fields get a number input.
      if (el.type === 'table' && el.source === 'library') {
        const baseLabel = el.label || eid;
        out.push({
          name: `${eid}__total`,
          label: `${baseLabel} - total rows`,
          type: 'number', synthType: 'number',
          options: null, required: false,
        });
        out.push({
          name: `${eid}__selected_count`,
          label: `${baseLabel} - selected count`,
          type: 'number', synthType: 'number',
          options: null, required: false,
        });
        out.push({
          name: `${eid}__all_selected`,
          label: `${baseLabel} - all selected`,
          type: 'boolean', synthType: 'boolean',
          options: null, required: false,
        });
        out.push({
          name: `${eid}__any_selected`,
          label: `${baseLabel} - any selected`,
          type: 'boolean', synthType: 'boolean',
          options: null, required: false,
        });
        // 0.36.717 — Table publishes this while a search is active.
        // Admin keys rules off it to hide/lock filter-source elements
        // for the duration of the search.
        out.push({
          name: `${eid}__search_active`,
          label: `${baseLabel} - search active`,
          type: 'boolean', synthType: 'boolean',
          options: null, required: false,
        });
      }
      // 0.38.67 - Payment card publishes six string sub-fields plus
      // a `sides` selector as dotted rule targets so admin can drive
      // each string (set_text) or the visible-sides toggle from rules
      // and button commands: `<eid>.cash_heading`, `.cash_description`,
      // `.cash_amount`, `.card_heading`, `.card_description`,
      // `.card_amount`, `.sides`. The renderer reads these back from
      // ruleState.texts and ctx.values.
      if (el.type === 'payment_card') {
        const baseLabel = el.label || eid;
        for (const [field, human] of [
          ['cash_heading', 'cash heading'],
          ['cash_description', 'cash description'],
          ['cash_amount', 'cash amount'],
          ['card_heading', 'card heading'],
          ['card_description', 'card description'],
          ['card_amount', 'card amount'],
          ['sides', 'visible sides (both/cash/card)'],
        ]) {
          out.push({
            name: `${eid}.${field}`,
            label: `${baseLabel} - ${human}`,
            type: 'text', synthType: 'text',
            options: null, required: false,
          });
        }
      }
      // 0.46.40 - Guide card: its click and its texts as dotted targets.
      if (el.type === 'guide_card') {
        const baseLabel = el.label || eid;
        for (const [field, human] of [
          ['action', 'card (clicked)'],
          ['badge', 'badge'],
          ['title', 'title'],
          ['text', 'text'],
        ]) {
          out.push({
            name: `${eid}.${field}`,
            label: `${baseLabel} - ${human}`,
            type: 'text', synthType: 'text',
            options: null, required: false,
          });
        }
      }
      // 0.38.69 - Information card publishes its badge/title/id, the
      // show_edit / show_action toggles, edit/action labels, and 8
      // section label/value pairs as dotted rule targets. set_text on
      // `<eid>.title`, `<eid>.section_3_value`, `<eid>.show_action`
      // ('true'/'false'), etc.
      // 0.47.28 - A chip's colors as rule targets (its text is the
      // chip's own id, as for a title).
      if (el.type === 'chip') {
        for (const [field, human] of [['fill_color', 'fill color'], ['text_color', 'text color'], ['stroke_color', 'stroke color']]) {
          out.push({ name: `${eid}.${field}`, label: `${el.label || eid} - ${human}`,
                     type: 'text', synthType: 'text', options: null, required: false });
        }
      }
      if (el.type === 'information_card') {
        const baseLabel = el.label || eid;
        const infoFields = [
          // 0.41.94 - The two CLICK ids the card fires, as distinct
          // from the `show_*` toggles and labels below. Without these
          // a rule reacting to one of the card's buttons is reported
          // as referencing an element that does not exist.
          ['edit', 'Edit button (clicked)'],
          ['action', 'action button (clicked)'],
          ['badge', 'badge'],
          ['title', 'title'],
          ['id_text', 'ID line'],
          // 0.47.21 - The icon's text, and the text colors (a Henaket
          // token such as --color-error, or #RRGGBB).
          ['icon_text', 'icon description'],
          ['text_color', 'all lines color'],
          ['badge_text_color', 'badge text color'],
          ['title_color', 'title color'],
          ['id_text_color', 'main description color'],
          ['icon_text_color', 'icon description color'],
          ['section_label_color', 'all section labels color'],
          ['section_value_color', 'all section values color'],
          ['show_edit', 'show Edit button (true/false)'],
          ['edit_label', 'Edit button label'],
          ['show_action', 'show action button (true/false)'],
          ['action_label', 'action button label'],
        ];
        // 0.38.96 - Only expose section fields up to the card's
        // section_count (was a flat 15, which showed rows the card
        // no longer has). Missing section_count defaults to 8 (the
        // legacy fixed count), matching the renderers. Clamped 1..15.
        const _secCount = (() => {
          const c = parseInt(el.section_count, 10);
          return Number.isFinite(c) ? Math.min(Math.max(c, 1), 15) : 8;
        })();
        for (let n = 1; n <= _secCount; n++) {
          infoFields.push([`section_${n}_label`, `section ${n} label`]);
          infoFields.push([`section_${n}_value`, `section ${n} value`]);
          infoFields.push([`section_${n}_label_color`, `section ${n} label color`]);
          infoFields.push([`section_${n}_value_color`, `section ${n} value color`]);
        }
        for (const [field, human] of infoFields) {
          out.push({
            name: `${eid}.${field}`,
            label: `${baseLabel} - ${human}`,
            type: 'text', synthType: 'text',
            options: null, required: false,
          });
        }
      }
    }
    if (Array.isArray(el.children)) {
      out.push(...collectNamedFields(el.children));
    }
  }
  return out;
}

/* ------------------------------------------------------------------
 * Rule summary — short human-readable description for the card list.
 *
 * Reads better than the raw JSON for non-developer admins. Format:
 *   "When <field> <op> <value>, <effect> <fields>"
 *
 * Falls back to "..." for parts we can't summarize cleanly (an empty
 * `when.all`, unknown operators, very long field lists).
 * ------------------------------------------------------------------ */

const OP_LABEL = {
  equals:       'is',
  not_equals:   'is not',
  // 0.47.73 - Number comparisons.
  less_than:    'is less than',
  more_than:    'is more than',
  at_least:     'is at least',
  at_most:      'is at most',
  in:           'is one of',
  is_set:       'is filled',
  is_empty:     'is empty',
  // 0.37.28 — Value-differs-from-default operators. Wording
  // mirrors the rule-editor dropdown for consistency.
  is_changed:   'was changed',
  is_unchanged: 'was not changed',
  // 0.37.32 — Button click edge trigger.
  just_clicked: 'was just clicked',
  // 0.37.42 — Form-level operator; the label reads as a full
  // sentence so the summarizer doesn't need special-casing.
  all_required_filled: 'all required fields are filled',
  // 0.37.47 — Negation.
  not_all_required_filled: 'not all required fields are filled',
};

const EFFECT_LABEL = {
  show:         'show',
  hide:         'hide',
  set_required: 'set required for',
  clear_value:  'clear value of',
  set_value:    'set value of',
  open_modal:   'open modal',
  close_modal:  'close modal',
  set_row:      'set clicked row of',
  // 0.48.89
  server_copy_fill:  'fill server copy',
  server_copy_set:   'change server copy',
  server_copy_reset: 'reset server copy',
  lock:         'lock for editing',
  set_label:    'set label of',
  set_action_value: 'set action value of',
  set_sort_order: 'set sort order of',
  add_option:   'add option to',
  remove_option: 'remove options from',
  // 0.37.47 — Error-message effects.
  set_error_message:  'set error message on',
  show_error_message: 'show error message on',
  hide_error_message: 'hide error message on',
  // 0.37.48 — Reset the "changed" baseline for a field.
  mark_unchanged:     'mark unchanged',
  restore_value:      'restore the unchanged value of',
};

function shortList(items, max = 2) {
  if (!Array.isArray(items) || items.length === 0) return '';
  if (items.length <= max) return items.join(', ');
  return `${items.slice(0, max).join(', ')} +${items.length - max} more`;
}

function describeLeaf(leaf, fieldByName) {
  if (!leaf) return '...';
  // 0.37.42 → 0.37.47 — Form-level operators have no field. Return
  // the label as a full sentence — OP_LABEL is already the full
  // English form, no field / value formatting needed.
  if (leaf.op === 'all_required_filled' || leaf.op === 'not_all_required_filled') {
    return OP_LABEL[leaf.op];
  }
  // 0.41.31 - An api_* leaf's field is a CONNECTION. Looking it up in
  // fieldByName is wrong twice over: usually it finds nothing and the
  // summary shows a bare slug, but if the form happens to contain an
  // ELEMENT of the same name it shows THAT element's label - a
  // summary describing something the rule does not test.
  // 0.48.29 - The result of create account.
  if (leaf.op === 'account_created') return 'account was created';
  if (leaf.op === 'account_pending') return 'account is waiting for approval';
  if (leaf.op === 'account_running') return 'account is being created';
  if (leaf.op === 'account_code_sent') return 'account is waiting for the email code';
  if (leaf.op === 'account_failed') {
    return leaf.value ? `account was not created with ${leaf.value}` : 'account was not created';
  }
  // 0.48.52 - The result of update account.
  if (leaf.op === 'account_updated') return 'account was updated';
  if (leaf.op === 'account_update_running') return 'account is being updated';
  if (leaf.op === 'account_update_code_sent') return 'account update is waiting for the email code';
  if (leaf.op === 'account_update_failed') {
    return leaf.value ? `account was not updated with ${leaf.value}` : 'account was not updated';
  }
  if (leaf.op === 'api_pending' || leaf.op === 'api_ok'
      || leaf.op === 'api_failed' || leaf.op === 'api_answer_is') {
    const conn = leaf.field || '(connection)';
    if (leaf.op === 'api_answer_is') {
      return `API ${conn} answer ${leaf.path || '(field)'} is ${leaf.value || '...'}`;
    }
    if (leaf.op === 'api_pending') return `API ${conn} is running`;
    if (leaf.op === 'api_ok') return `API ${conn} succeeded`;
    return leaf.value
      ? `API ${conn} failed with ${leaf.value}`
      : `API ${conn} failed`;
  }
  if (!leaf.field) return '...';
  const fieldDef = fieldByName.get(leaf.field);
  const fieldDisplay = fieldDef ? fieldDef.label : leaf.field;
  const op = OP_LABEL[leaf.op] || leaf.op || '?';
  // is_set / is_empty take no value
  if (leaf.op === 'is_set' || leaf.op === 'is_empty') {
    return `${fieldDisplay} ${op}`;
  }
  if (leaf.op === 'in') {
    const arr = Array.isArray(leaf.value) ? leaf.value : [];
    return `${fieldDisplay} ${op} [${shortList(arr.map(String))}]`;
  }
  return `${fieldDisplay} ${op} "${leaf.value ?? ''}"`;
}

function describeEffect(effect, fieldByName) {
  if (!effect || !effect.effect) return '...';
  const verb = EFFECT_LABEL[effect.effect] || effect.effect;
  const fields = Array.isArray(effect.fields) ? effect.fields : [];
  // 0.37.47 — Sentinel target: `__all_required__` expands at
  // evaluation time to every required field in the form. In the
  // summary it reads as "all required fields" so admin sees the
  // intent, not the sentinel string.
  const targetLabels = fields.map(name => {
    if (name === '__all_required__') return 'all required fields';
    const def = fieldByName.get(name);
    return def ? def.label : name;
  });
  const targets = shortList(targetLabels);
  if (effect.effect === 'set_required') {
    const req = effect.required ? 'required' : 'optional';
    return `make ${req}: ${targets || '(no targets)'}`;
  }
  if (effect.effect === 'set_value') {
    return `set ${targets || '(no targets)'} to "${effect.value ?? ''}"`;
  }
  if (effect.effect === 'set_error_message') {
    return `set error on ${targets || '(no targets)'} to "${effect.message ?? ''}"`;
  }
  // 0.38.87 - show_required_errors has no target verb.
  if (effect.effect === 'show_required_errors') {
    return 'show errors on empty required fields';
  }
  // 0.38.84 - scroll_to reads its kind, not a target verb.
  if (effect.effect === 'scroll_to') {
    const kind = effect.scroll_kind || 'first_error';
    if (kind === 'top') return 'scroll to top of form';
    if (kind === 'first_error') return 'scroll to first error';
    return `scroll to ${targets || '(no field)'}`;
  }
  return `${verb} ${targets || '(no targets)'}`;
}

/**
 * 0.38.82 - Collect every field / target reference in a rule that no
 * longer exists in the form's current element set. Returns a sorted
 * array of the missing names (empty when the rule is clean). Used to
 * flag rules whose conditions or effects point at deleted elements -
 * e.g. `mark_unchanged multiselect_1` after multiselect_1 was
 * removed. The plain-text summary can't be coloured, so the rule
 * list shows a warning badge based on this.
 *
 * Sentinels ('__form__', '__all_required__', '') and the dotted
 * sub-field targets of card elements (`card.title` etc.) are NOT
 * flagged: the card sub-fields are synthetic and resolve at render
 * time. We only flag a dotted target when its base element_id
 * (before the first dot) is unknown.
 */
export function ruleMissingRefs(rule, namedFields) {
  if (!rule || typeof rule !== 'object') return [];
  const known = new Set((namedFields || []).map(f => f.name));
  const baseKnown = new Set(
    (namedFields || []).map(f => String(f.name).split('.')[0]));
  const missing = new Set();

  const SENTINELS = new Set(['', '__form__', '__all_required__']);
  const checkName = (raw) => {
    if (raw == null) return;
    const name = String(raw);
    if (SENTINELS.has(name)) return;
    // 0.49.08 - A condition on a formula, not a name.
    if (name.startsWith('=')) return;
    // 0.49.20 - A value from the page's address.
    if (/^url\.[A-Za-z][A-Za-z0-9_]*$/.test(name)) return;
    if (known.has(name)) return;
    // Dotted target (card sub-field): OK if the base element exists.
    const base = name.split('.')[0];
    if (name.includes('.')) {
      if (known.has(base) || baseKnown.has(base)) return;
    }
    /* 0.48.89 - Names the page makes itself: a table's clicked row and
       edited rows (<table>_row, <table>_edited), a server copy's state
       and failure (<copy>_state, <copy>_failure). */
    /* 0.48.98 - and a table's ticked rows (<table>_checked, <table>_checked_count). */
    const made = /^(.+)_(row|edited|state|failure|refused|dirty|checked|checked_count)$/.exec(base);
    if (made && known.has(made[1])) return;
    missing.add(name);
  };

  // 0.41.29 - An api_* leaf's `field` is a CONNECTION reference name,
  // not an element id. Checking it against the form's elements finds
  // nothing and reports a perfectly good rule as broken.
  //
  // Connections are registered platform-wide and this function only
  // knows about one form, so it cannot verify the name exists - it
  // can only know not to look for it here.
  const API_OPS = new Set(['api_pending', 'api_ok', 'api_failed', 'api_answer_is',
    // 0.48.29 - field is "account", not an element.
    'account_created', 'account_pending', 'account_running', 'account_failed',
    'account_code_sent',
    // 0.48.52 - field is "account_update".
    'account_updated', 'account_update_running', 'account_update_code_sent', 'account_update_failed']);

  const scanLeaves = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const l of arr) {
      if (!l || typeof l !== 'object') continue;
      if (API_OPS.has(l.op)) continue;
      if (l.field) checkName(l.field);
    }
  };
  const scanEffects = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const e of arr) {
      if (!e || typeof e !== 'object') continue;
      if (Array.isArray(e.fields)) e.fields.forEach(checkName);
      if (e.field) checkName(e.field);
      // 0.41.29 - call_api binds inputs to ELEMENTS, and those
      // references deserve the same check as any other: an input
      // pointing at a deleted element silently sends an empty value.
      //
      // Its `connection` is NOT checked - that names a registered
      // connection, not an element.
      if ((e.effect === 'call_api' || e.effect === 'create_account' || e.effect === 'update_account') && Array.isArray(e.inputs)) {
        for (const row of e.inputs) {
          if (row && row.source !== 'literal' && row.field) checkName(row.field);
        }
      }
      // 0.48.29 - create account's own fields.
      if (e.effect === 'create_account') {
        if (e.email_field) checkName(e.email_field);
        if (e.password_field) checkName(e.password_field);
        if (e.confirm_field) checkName(e.confirm_field);
        for (const m of (Array.isArray(e.match) ? e.match : [])) if (m && m.field) checkName(m.field);
      }
      // 0.48.38
      if (e.effect === 'confirm_account_code' && e.code_field) checkName(e.code_field);
      // 0.48.52 - update account's page fields.
      if (e.effect === 'update_account' && e.set && typeof e.set === 'object') {
        for (const spec of Object.values(e.set)) {
          if (spec && spec.source === 'field' && spec.field) checkName(spec.field);
        }
      }
    }
  };

  scanLeaves(rule.when && rule.when.all);
  scanLeaves(rule.when && rule.when.any);
  // 0.47.75 - OR groups.
  (Array.isArray(rule.when && rule.when.groups) ? rule.when.groups : [])
    .forEach(g => scanLeaves(g && g.all));
  scanLeaves(rule.else_when && rule.else_when.all);
  scanLeaves(rule.else_when && rule.else_when.any);
  scanEffects(rule.then);
  scanEffects(rule.else);

  return Array.from(missing).sort();
}

export function summarizeRule(rule, namedFields) {
  if (!rule || typeof rule !== 'object') return '(empty rule)';
  const fieldByName = new Map((namedFields || []).map(f => [f.name, f]));
  const whenAll = Array.isArray(rule.when && rule.when.all) ? rule.when.all : [];
  const whenAny = Array.isArray(rule.when && rule.when.any) ? rule.when.any : [];
  const thenList = Array.isArray(rule.then) ? rule.then : [];

  const parts = [];
  if (whenAll.length > 0) {
    parts.push(whenAll.map(l => describeLeaf(l, fieldByName)).join(' AND '));
  }
  if (whenAny.length > 0) {
    const orStr = whenAny.map(l => describeLeaf(l, fieldByName)).join(' OR ');
    parts.push(whenAny.length > 1 || whenAll.length > 0 ? `(${orStr})` : orStr);
  }
  // 0.47.75 - OR groups: (a AND b) OR (c AND d).
  const groups = (Array.isArray(rule.when && rule.when.groups) ? rule.when.groups : [])
    .filter(g => g && Array.isArray(g.all) && g.all.length > 0);
  if (groups.length > 0) {
    const gs = groups.map(g => `(${g.all.map(l => describeLeaf(l, fieldByName)).join(' AND ')})`).join(' OR ');
    parts.push(parts.length > 0 && groups.length > 1 ? `(${gs})` : gs);
  }
  const whenStr = parts.length === 0 ? '(no condition)' : parts.join(' AND ');
  const thenStr = thenList.length === 0
    ? '(no effects)'
    : thenList.map(e => describeEffect(e, fieldByName)).join('; ');

  // 0.36.109 — Append else summary when present. Includes else_when
  // gating conditions if any are set.
  let elseStr = '';
  if (Array.isArray(rule.else) && rule.else.length > 0) {
    const elseEffectsStr = rule.else.map(e => describeEffect(e, fieldByName)).join('; ');
    const ewAll = Array.isArray(rule.else_when && rule.else_when.all) ? rule.else_when.all : [];
    const ewAny = Array.isArray(rule.else_when && rule.else_when.any) ? rule.else_when.any : [];
    if (ewAll.length === 0 && ewAny.length === 0) {
      // Legacy else: fires on the inverse of when.
      elseStr = `; else → ${elseEffectsStr}`;
    } else {
      const ewParts = [];
      if (ewAll.length > 0) {
        ewParts.push(ewAll.map(l => describeLeaf(l, fieldByName)).join(' AND '));
      }
      if (ewAny.length > 0) {
        const ewOrStr = ewAny.map(l => describeLeaf(l, fieldByName)).join(' OR ');
        ewParts.push(ewAny.length > 1 || ewAll.length > 0 ? `(${ewOrStr})` : ewOrStr);
      }
      elseStr = `; else when ${ewParts.join(' AND ')} → ${elseEffectsStr}`;
    }
  }

  return `When ${whenStr} → ${thenStr}${elseStr}`;
}

/* ------------------------------------------------------------------
 * Validation — friendlier admin-facing messages for the raw JSON
 * panel. The runtime evaluator is permissive (silently no-ops on
 * malformed input); this validator surfaces the actual mistake so
 * admins can fix it.
 * ------------------------------------------------------------------ */

// 0.36.108 — Added not_in to round out the negation suite alongside
// not_equals. Useful for "field is not one of these values" without
// needing two separate not_equals leaves.
// 0.41.16 - Derived from schema.js, not hand-listed.
//
// These were two hardcoded sets, last touched in 0.36.108. Everything
// added since - just_clicked, is_changed, all_required_filled,
// set_label, set_text, set_variant, the table effects, and now
// call_api - was REJECTED by the validator while working perfectly at
// runtime. A rule the engine executes correctly could not be saved.
//
// A third copy of the vocabulary was always going to drift. The
// schema is the one place that defines it, so these read from it.
const ALLOWED_OPS = new Set(OPERATORS);
const ALLOWED_EFFECTS = new Set(EFFECTS);

/**
 * Returns null if the rules array is well-formed, otherwise a
 * descriptive error string. Catches the common mistakes we've seen
 * admins make (double-wrapped arrays, `< >` placeholder syntax left
 * in field names, unknown ops/effects, missing required keys).
 */
export function validateRulesArray(rules) {
  if (!Array.isArray(rules)) {
    return `Rules must be a JSON array. Got ${typeof rules}.`;
  }
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    if (Array.isArray(rule)) {
      return `Rule ${i + 1} is an array, not an object. ` +
        `It looks like the rule got wrapped in extra brackets — try removing one layer of "[" "]".`;
    }
    if (!rule || typeof rule !== 'object') {
      return `Rule ${i + 1} must be a JSON object (\`{...}\`). Got ${rule === null ? 'null' : typeof rule}.`;
    }
    if (typeof rule.id !== 'string' || rule.id === '') {
      return `Rule ${i + 1} is missing the "id" field (a unique string).`;
    }
    if (typeof rule.name !== 'string') {
      return `Rule ${i + 1} ("${rule.id}") is missing the "name" field.`;
    }
    // when block — 0.36.108 — now supports BOTH `all` (AND) and `any`
    // (OR) clauses. Either may be missing/empty. At least one
    // non-empty clause is required (otherwise the rule never fires).
    if (!rule.when || typeof rule.when !== 'object' || Array.isArray(rule.when)) {
      return `Rule ${i + 1} ("${rule.id}") needs a "when" object with an "all" and/or "any" array.`;
    }
    const all = rule.when.all;
    const any = rule.when.any;
    const hasAll = all !== undefined && all !== null;
    const hasAny = any !== undefined && any !== null;
    // 0.47.75 - OR groups: when.groups = [{ all: [leaf, ...] }, ...].
    const groups = rule.when.groups;
    const hasGroups = groups !== undefined && groups !== null;
    if (hasGroups && !Array.isArray(groups)) {
      return `Rule ${i + 1} ("${rule.id}").when.groups must be an array of { "all": [...] } groups.`;
    }
    if (!hasAll && !hasAny && !hasGroups) {
      return `Rule ${i + 1} ("${rule.id}").when needs an "all", "any" or "groups" array.`;
    }
    if (hasAll && !Array.isArray(all)) {
      return `Rule ${i + 1} ("${rule.id}").when.all must be an array.`;
    }
    if (hasAny && !Array.isArray(any)) {
      return `Rule ${i + 1} ("${rule.id}").when.any must be an array.`;
    }
    // Both clauses use the same leaf shape — validate uniformly.
    const validateClause = (leaves, clauseName) => {
      for (let j = 0; j < leaves.length; j++) {
        const leaf = leaves[j];
        const path = `when.${clauseName}[${j}]`;
        if (!leaf || typeof leaf !== 'object') {
          return `Rule ${i + 1} ("${rule.id}").${path} must be an object.`;
        }
        // 0.48.30 - Whole-form conditions name no field. The JSON view
        // refused them ("missing field"), so a rule using "all required
        // fields are filled" could not be pasted or edited as JSON.
        if (['all_required_filled', 'not_all_required_filled', 'always', 'never', 'page_loads'].includes(leaf.op)) continue;
        if (typeof leaf.field !== 'string' || leaf.field === '') {
          return `Rule ${i + 1} ("${rule.id}").${path} missing "field".`;
        }
        if (leaf.field.startsWith('<') && leaf.field.endsWith('>')) {
          return `Rule ${i + 1} ("${rule.id}").${path}.field is "${leaf.field}" — ` +
            `the angle brackets are example syntax, not part of the actual field name. ` +
            `Remove the < > and use just the field name (e.g. "${leaf.field.slice(1, -1)}").`;
        }
        if (!ALLOWED_OPS.has(leaf.op)) {
          return `Rule ${i + 1} ("${rule.id}").${path}.op is "${leaf.op}". ` +
            `Allowed: ${[...ALLOWED_OPS].join(', ')}.`;
        }
        if (typeof leaf.value === 'string' && leaf.value.startsWith('<') && leaf.value.endsWith('>')) {
          return `Rule ${i + 1} ("${rule.id}").${path}.value is "${leaf.value}" — ` +
            `the angle brackets are example syntax. Use the actual stored value, e.g. "${leaf.value.slice(1, -1)}".`;
        }
      }
      return null;
    };
    if (hasAll) {
      const err = validateClause(all, 'all');
      if (err) return err;
    }
    if (hasAny) {
      const err = validateClause(any, 'any');
      if (err) return err;
    }
    if (hasGroups) {
      for (let g = 0; g < groups.length; g++) {
        if (!groups[g] || !Array.isArray(groups[g].all)) {
          return `Rule ${i + 1} ("${rule.id}").when.groups[${g}] needs an "all" array.`;
        }
        const err = validateClause(groups[g].all, `groups[${g}].all`);
        if (err) return err;
      }
    }
    // then block
    if (!Array.isArray(rule.then)) {
      return `Rule ${i + 1} ("${rule.id}").then must be an array of effects.`;
    }
    for (let j = 0; j < rule.then.length; j++) {
      const err = validateEffect(rule.then[j], i, rule.id, 'then', j);
      if (err) return err;
    }
    // else (optional) — array of effects fired when the main `when`
    // doesn't match. 0.36.109 — gated by optional `else_when` clause.
    if (rule.else !== undefined) {
      if (!Array.isArray(rule.else)) {
        return `Rule ${i + 1} ("${rule.id}").else must be an array of effects (when present).`;
      }
      for (let j = 0; j < rule.else.length; j++) {
        const err = validateEffect(rule.else[j], i, rule.id, 'else', j);
        if (err) return err;
      }
    }
    // else_when (optional) — same shape as `when`: object with `all`
    // and/or `any` arrays. Only meaningful when `else` is also present.
    if (rule.else_when !== undefined && rule.else_when !== null) {
      if (typeof rule.else_when !== 'object' || Array.isArray(rule.else_when)) {
        return `Rule ${i + 1} ("${rule.id}").else_when must be an object with "all" and/or "any" arrays.`;
      }
      const ewAll = rule.else_when.all;
      const ewAny = rule.else_when.any;
      if (ewAll !== undefined && !Array.isArray(ewAll)) {
        return `Rule ${i + 1} ("${rule.id}").else_when.all must be an array.`;
      }
      if (ewAny !== undefined && !Array.isArray(ewAny)) {
        return `Rule ${i + 1} ("${rule.id}").else_when.any must be an array.`;
      }
      if (Array.isArray(ewAll)) {
        const err = validateClause(ewAll, 'all');  // reuse, prefix in path
        if (err) return err.replace('when.all', 'else_when.all');
      }
      if (Array.isArray(ewAny)) {
        const err = validateClause(ewAny, 'any');
        if (err) return err.replace('when.any', 'else_when.any');
      }
    }
  }
  return null;
}

function validateEffect(effect, ruleIdx, ruleId, branch, effectIdx) {
  if (!effect || typeof effect !== 'object') {
    return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}] must be an object.`;
  }
  if (!ALLOWED_EFFECTS.has(effect.effect)) {
    return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].effect is "${effect.effect}". ` +
      `Allowed: ${[...ALLOWED_EFFECTS].join(', ')}.`;
  }
  // 0.41.16 - call_api targets no element, so it has no `fields`. It
  // is validated on its own terms: a connection, and input rows that
  // each name something and say where the value comes from.
  if (effect.effect === 'call_api') {
    if (typeof effect.connection !== 'string' || !effect.connection.trim()) {
      return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].connection `
        + 'must be the reference name of an API connection.';
    }
    if (effect.inputs !== undefined && !Array.isArray(effect.inputs)) {
      return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].inputs `
        + 'must be an array.';
    }
    for (let k = 0; k < (effect.inputs || []).length; k++) {
      const row = effect.inputs[k];
      if (!row || typeof row !== 'object') {
        return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].inputs[${k}] `
          + 'must be an object.';
      }
      if (typeof row.name !== 'string' || !row.name.trim()) {
        return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].inputs[${k}].name `
          + 'is required - it must match an input the connection expects.';
      }
      if (row.source !== 'field' && row.source !== 'literal') {
        return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].inputs[${k}].source `
          + 'must be "field" or "literal".';
      }
      if (row.source === 'field' && (typeof row.field !== 'string' || !row.field.trim())) {
        return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].inputs[${k}].field `
          + 'is required when source is "field".';
      }
    }
    return null;
  }
  // 0.48.29 - create_account: an email and a password field; the API,
  // when there is one, with what must match.
  if (effect.effect === 'create_account') {
    const at = `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}]`;
    if (branch !== 'then') return `${at}: create account runs only in Then.`;
    if (typeof effect.email_field !== 'string' || !effect.email_field.trim()) return `${at}.email_field is required.`;
    if (typeof effect.password_field !== 'string' || !effect.password_field.trim()) return `${at}.password_field is required.`;
    if (effect.match !== undefined && !Array.isArray(effect.match)) return `${at}.match must be an array of { path, field }.`;
    for (const m of (effect.match || [])) {
      if (!m || !String(m.path || '').trim() || !String(m.field || '').trim()) return `${at}.match needs a path and a field in each row.`;
    }
    if (effect.approval !== undefined && !['wait', 'active'].includes(effect.approval)) return `${at}.approval must be "wait" or "active".`;
    return null;
  }
  // 0.48.52 - update_account: at least one value to set, each from an
  // API path (which needs the API) or a page field.
  if (effect.effect === 'update_account') {
    const at = `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}]`;
    if (branch !== 'then') return `${at}: update account runs only in Then.`;
    const set = effect.set && typeof effect.set === 'object' ? effect.set : {};
    const keys = Object.keys(set).filter(k => set[k]);
    if (!keys.length) return `${at}: update account needs at least one of email, first name, last name.`;
    for (const k of keys) {
      if (!['email', 'first_name', 'last_name'].includes(k)) return `${at}.set.${k}: only email, first_name and last_name can be updated.`;
      const spec = set[k];
      if (spec.source === 'field' ? !String(spec.field || '').trim() : !String(spec.path || '').trim()) {
        return `${at}.set.${k} needs ${spec.source === 'field' ? 'a field' : 'an API path'}.`;
      }
      if (spec.source !== 'field' && !String(effect.connection || '').trim()) {
        return `${at}.set.${k} reads the API, so the effect needs a connection.`;
      }
    }
    if (effect.ssn_path && !String(effect.connection || '').trim()) return `${at}.ssn_path needs a connection.`;
    return null;
  }
  // 0.48.30 - Effects that target no element.
  if (['show_required_errors', 'flow_next', 'flow_previous', 'flow_first', 'flow_restart',
       'resend_account_code'].includes(effect.effect)) return null;
  // 0.48.38 - The code's field.
  if (effect.effect === 'confirm_account_code') {
    return String(effect.code_field || '').trim() ? null
      : `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].code_field is required.`;
  }
  if (effect.effect === 'set_flow_error') {
    return String(effect.error_id || '').trim() ? null
      : `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].error_id is required.`;
  }
  if (effect.effect === 'scroll_to' && (effect.scroll_kind || 'first_error') !== 'element') return null;
  // 0.41.25 - set_from_api DOES have target fields, but also needs a
  // connection and a path. Without these checks a rule would save and
  // then quietly set nothing.
  if (effect.effect === 'set_from_api') {
    if (typeof effect.connection !== 'string' || !effect.connection.trim()) {
      return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].connection `
        + 'must be the reference name of an API connection.';
    }
    if (typeof effect.path !== 'string' || !effect.path.trim()) {
      return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].path `
        + 'is required, e.g. total_matches or results[].mark_id.';
    }
  }
  if (!Array.isArray(effect.fields) || effect.fields.length === 0) {
    return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].fields must be a non-empty array.`;
  }
  for (let k = 0; k < effect.fields.length; k++) {
    const f = effect.fields[k];
    if (typeof f !== 'string' || f === '') {
      return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].fields[${k}] must be a non-empty string.`;
    }
    if (f.startsWith('<') && f.endsWith('>')) {
      return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}].fields[${k}] is "${f}" — ` +
        `the angle brackets are example syntax. Use just the field name, e.g. "${f.slice(1, -1)}".`;
    }
  }
  if (effect.effect === 'set_required' && typeof effect.required !== 'boolean') {
    return `Rule ${ruleIdx + 1} ("${ruleId}").${branch}[${effectIdx}] needs "required": true/false.`;
  }
  return null;
}

/* ------------------------------------------------------------------
 * ID generator for new rules / leaves / effects.
 * ------------------------------------------------------------------ */

let _idCounter = 0;
export function newRuleId(existingRules) {
  // Find the smallest positive integer not already used as an id.
  const used = new Set();
  for (const r of (existingRules || [])) {
    if (r && typeof r.id === 'string') {
      const n = parseInt(r.id, 10);
      if (!Number.isNaN(n)) used.add(n);
    }
  }
  let i = 1;
  while (used.has(i)) i++;
  return String(i);
}

// 0.37.36 — True if the rule's when-clause references the
// `just_clicked` operator anywhere (top-level all/any, or in
// else_when). Used by consumers to decide whether the rule's
// reactive effects (set_label today; hide/show/lock/etc.
// candidates for future ships) should be applied as sticky
// state that outlives the one-render click window, rather than
// reverting when just_clicked flips back to false a tick later.
export function ruleHasClickTrigger(rule) {
  if (!rule || typeof rule !== 'object') return false;
  const scan = (clause) => {
    if (!clause || typeof clause !== 'object') return false;
    const all = Array.isArray(clause.all) ? clause.all : [];
    const any = Array.isArray(clause.any) ? clause.any : [];
    for (const leaf of all) if (leaf && leaf.op === 'just_clicked') return true;
    for (const leaf of any) if (leaf && leaf.op === 'just_clicked') return true;
    // 0.47.75 - OR groups.
    for (const g of (Array.isArray(clause.groups) ? clause.groups : [])) {
      for (const leaf of ((g && Array.isArray(g.all)) ? g.all : [])) {
        if (leaf && leaf.op === 'just_clicked') return true;
      }
    }
    return false;
  };
  return scan(rule.when) || scan(rule.else_when);
}


/** 0.48.91 - plus server copies (the page's Settings, and the flows the
 * page is a step of), so the server copy effects can target one and a
 * rule reading `adv.result...` is not reported as naming something
 * missing. `copies`: [{ name, editable }]. */
/** 0.48.93 - Paths a rule's "change server copy" names that its copy
 * does not allow (Settings, Server copies): the server would refuse the
 * whole change. [{ copy, path }]. */
export function ruleCopyPathProblems(rule, namedFields) {
  const out = [];
  const copies = new Map((namedFields || []).filter(f => f.type === 'server_copy').map(f => [f.name, f.editable || []]));
  const inside = (path, allowed) => allowed.some(a => path === a || path.startsWith(`${a}.`) || path.startsWith(`${a}[`));
  for (const list of [rule && rule.then, rule && rule.else]) {
    for (const eff of (Array.isArray(list) ? list : [])) {
      if (!eff || eff.effect !== 'server_copy_set') continue;
      for (const name of (eff.fields || [])) {
        if (!copies.has(name)) continue;
        for (const v of (eff.values || [])) {
          const path = String((v && v.path) || '').trim();
          if (path && !inside(path, copies.get(name))) out.push({ copy: name, path });
        }
      }
    }
  }
  return out;
}

export function withServerCopies(namedFields, copies) {
  const out = [...(namedFields || [])];
  const have = new Set(out.map(f => f.name));
  for (const c of (copies || [])) {
    if (!c || !c.name || have.has(c.name)) continue;
    have.add(c.name);
    out.push({ name: c.name, label: `Server copy: ${c.name}${c.flow ? ` (flow ${c.flow})` : ''}`,
               type: 'server_copy', options: null, required: false, serverCopy: true,
               editable: c.editable || [] });
  }
  return out;
}

/** 0.45.96 - The form's fields plus its own variables (Settings,
 * Variables), so a rule can set or test one without being reported as
 * naming something missing. */
export function withFormVariables(namedFields, variables) {
  const out = [...(namedFields || [])];
  const have = new Set(out.map(f => f.name));
  for (const v of (variables || [])) {
    if (!v || have.has(v)) continue;
    out.push({ name: v, label: `Variable: ${v}`, type: 'text',
               options: null, required: false, formVariable: true });
  }
  return out;
}
